import type { BrandVoice, BrandVoiceField, BrandVoiceFieldSource, DerivedTone, DerivedVoice } from '../../services/api'

/**
 * The voice review's vocabulary and its pure rules — shared by the review card
 * (`DerivedVoiceCard`, How we sound) and the Brand overview (MAR-384), so the two can never
 * label, date or judge one derivation two ways. Moved verbatim out of `DerivedVoiceCard`.
 */

/**
 * The server's own in-flight window. Past it, whatever ran is not coming back.
 *
 * ⚠️ **This is a COPY of `DERIVATION_IN_FLIGHT_MS`** in
 * `services/governance/governance-api/src/index.js` (search that name), and nothing enforces
 * that the two agree — the review payload carries the marker, not the window. Drift is silent
 * and shows up as a card that gives up while a run is still alive, or one that polls a dead
 * run for as long as the tab is open. Move both, or neither.
 */
export const DERIVATION_WINDOW_MS = 6 * 60 * 1000
export const POLL_MS = 5000

/**
 * ⚠️ The five she may PROPOSE, which is not the six the merchant may HOLD.
 *
 * `TONE_ORDER` is the correction control, so it is `DerivedTone` and stays that way: the
 * deriver cannot propose `custom`, and a chip offering it would confirm a tone with no prose
 * behind it — `buildBrandVoiceInstructions` then renders `balanced`, a voice neither party
 * chose. `DISPLAY_TONE_LABELS` is the other half: the merchant CAN be on `custom` (the form
 * below this card sets it), and a display map that cannot name it is how the card came to
 * report a confirmed custom tone as the derived one. Correctable and displayable are two
 * types, not one type and a cast.
 */
export const TONE_LABELS: Record<DerivedTone, string> = {
  friendly: 'Friendly',
  professional: 'Professional',
  casual: 'Casual',
  luxurious: 'Luxurious',
  playful: 'Playful'
}
export const TONE_ORDER: DerivedTone[] = ['friendly', 'casual', 'playful', 'professional', 'luxurious']

export type DisplayTone = BrandVoice['tone']
export const DISPLAY_TONE_LABELS: Record<DisplayTone, string> = {
  ...TONE_LABELS,
  // Never "Custom" — the merchant did not pick a preset called Custom, they wrote the tone
  // out themselves, and the prose is shown under the grid.
  custom: 'Your own words'
}

export const FORMALITY_LABELS = ['Very casual', 'Casual', 'Balanced', 'Formal', 'Very formal']
export const formalityLabel = (level: number) => FORMALITY_LABELS[Math.min(5, Math.max(1, Math.round(level))) - 1]

export const EMOJI_LABELS: Record<BrandVoice['emojiUsage'], string> = {
  never: 'Never',
  sparingly: 'Sparingly',
  frequently: 'Frequently'
}
export const LENGTH_LABELS: Record<BrandVoice['responseLength'], string> = {
  concise: 'Concise',
  balanced: 'Balanced',
  detailed: 'Detailed'
}

/** The four, in the order the design shows them: Tone · Formality · Length · Emoji. */
export const TRAIT_ORDER = ['tone', 'formalityLevel', 'responseLength', 'emojiUsage'] as const
export type TraitKey = (typeof TRAIT_ORDER)[number]

export const TRAIT_LABELS: Record<TraitKey, string> = {
  tone: 'Tone',
  formalityLevel: 'Formality',
  responseLength: 'Length',
  emojiUsage: 'Emoji'
}

/**
 * What the grid renders — the CORRECTABLE four widened by the one state only the merchant can
 * reach. `CorrectableTraits` is what `correctVoice` accepts and must stay narrow; this is what
 * the card is able to say. Every `CorrectableTraits` is a valid `DisplayTraits`, and nothing
 * goes the other way, which is the point.
 */
export interface DisplayTraits {
  tone: DisplayTone
  /** Present only with `tone: 'custom'`, and even then only if the merchant wrote one. */
  customToneDescription?: string | null
  formalityLevel: number
  emojiUsage: BrandVoice['emojiUsage']
  responseLength: BrandVoice['responseLength']
}

export function traitValueLabel(trait: TraitKey, traits: DisplayTraits): string {
  switch (trait) {
    case 'tone': return DISPLAY_TONE_LABELS[traits.tone] ?? traits.tone
    case 'formalityLevel': return formalityLabel(traits.formalityLevel)
    case 'responseLength': return LENGTH_LABELS[traits.responseLength] ?? traits.responseLength
    case 'emojiUsage': return EMOJI_LABELS[traits.emojiUsage] ?? traits.emojiUsage
  }
}

/** A date the merchant would write. Never a time — nothing here turns on the hour. */
export function formatDay(iso?: string | null): string {
  if (!iso) return 'an unknown date'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return 'an unknown date'
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * The server's refusal, said in the merchant's terms.
 *
 * Keyed on `details.reason` — the stable token — never on `error`, which is prose. Same map
 * shape as `AuthorityPanel.refusalMessage`, and for the same reason: the client must not
 * pre-empt a decision the server makes.
 */
export function refusalMessage(reason: unknown, fallback: string): string {
  switch (reason) {
    case 'deriver_invoke_failed':
      return 'She could not start reading your storefront. Nothing was changed — try again, and tell us if it keeps happening.'
    case 'no_derivation':
      return 'There is nothing to confirm any more. Ask her to read your storefront again.'
    case 'derivation_invalid':
      return 'That reading cannot be used — something in it does not hold up. Ask her to read your storefront again.'
    case 'derived_at_required':
      return 'That did not go through. Reload the page and answer the reading in front of you.'
    case 'derivation_superseded':
      return 'She read your storefront again while you were reading this, so what you answered is no longer what she has. This is the newer one.'
    case 'already_confirmed':
      // ⚠️ NOT "there was nothing to correct". The server answers this token only when a trait
      // you sent DIFFERS from what is stored — so there was something to correct, and it was
      // refused and thrown away. The earlier wording told the merchant their edit was
      // unnecessary while it was in fact lost.
      return 'This reading was confirmed somewhere else while you were correcting it, so your correction was refused and nothing you changed was saved. This is where it stands now.'
    case 'invalid_correction':
      return 'Those corrections were refused. Tone, formality, length and emoji are the four she can hold.'
    default:
      return fallback
  }
}

/**
 * Is this block one the card can actually render?
 *
 * ⚠️ `GET …/governance/voice` answers the derived block **RAW**, on purpose: this route is the
 * review, and a proposal the merchant must be able to reject cannot be withheld because it is
 * malformed. The server says the same thing from the other side — `confirmVoiceDerivation`
 * anticipates a block that fails `BrandVoiceDerivedSchema` and answers a recoverable 422
 * `derivation_invalid` rather than a crash.
 *
 * So the card is the one thing standing between a partial row and a blank Voice tab. Reaching
 * into `traits.summary` or `provenance.sourceDomain` on a half-written block throws during
 * render and takes the tab down — including the "Read it again" button, which is the one act
 * that would fix it. Everything checked here is something the card DEREFERENCES; this is not a
 * second copy of the schema, and where they disagree the schema is right.
 */
export function usableDerivation(block: unknown): DerivedVoice | null {
  if (!block || typeof block !== 'object') return null
  const { traits, provenance } = block as { traits?: unknown; provenance?: unknown }
  if (!traits || typeof traits !== 'object') return null
  if (!provenance || typeof provenance !== 'object') return null

  const t = traits as Record<string, unknown>
  const p = provenance as Record<string, unknown>
  const inMap = (map: Record<string, string>, value: unknown) =>
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(map, value)

  const traitsUsable =
    typeof t.summary === 'string' &&
    // ⚠️ TONE_ORDER, not the display map: a STORED `custom` is not something she read, and a
    // proposal claiming she derived one is exactly the block that must not render as a reading.
    TONE_ORDER.includes(t.tone as DerivedTone) &&
    typeof t.formalityLevel === 'number' && Number.isFinite(t.formalityLevel) &&
    inMap(EMOJI_LABELS, t.emojiUsage) &&
    inMap(LENGTH_LABELS, t.responseLength)

  const provenanceUsable =
    typeof p.sourceDomain === 'string' && p.sourceDomain.length > 0 &&
    // The pin an act sends back. Without it there is nothing to confirm or correct.
    typeof p.derivedAt === 'string' && p.derivedAt.length > 0 &&
    typeof p.pageCount === 'number' && Number.isFinite(p.pageCount)

  return traitsUsable && provenanceUsable ? (block as DerivedVoice) : null
}

/** Did the merchant state this trait themselves? The marker is the switch. */
export function merchantStated(fieldSources: Partial<Record<BrandVoiceField, BrandVoiceFieldSource>> | undefined, key: TraitKey): boolean {
  const marker = fieldSources?.[key]
  return marker?.setBy === 'merchant' || marker?.merchantOverride === true
}

/**
 * ⚠️ What she USES, which after a correction is not what she READ — see the long note at the
 * call site in `DerivedVoiceCard`. A merchant-stated trait reads from the live (gated) voice;
 * everything else from her reading. Falls back to the reading when the live read failed.
 */
export function effectiveTraits(
  derived: DerivedVoice,
  fieldSources: Partial<Record<BrandVoiceField, BrandVoiceFieldSource>> | undefined,
  live: BrandVoice | null
): DisplayTraits {
  const traits = derived.traits
  const stated = (key: TraitKey) => merchantStated(fieldSources, key)
  const tone: DisplayTone = stated('tone') && live?.tone ? live.tone : traits.tone
  return {
    tone,
    customToneDescription: tone === 'custom' ? live?.customToneDescription ?? null : null,
    formalityLevel: stated('formalityLevel') && live ? live.formalityLevel : traits.formalityLevel,
    emojiUsage: stated('emojiUsage') && live ? live.emojiUsage : traits.emojiUsage,
    responseLength: stated('responseLength') && live ? live.responseLength : traits.responseLength
  }
}

/** "Tone: Friendly · Formality: Balanced · Emoji: Sparingly · Length: Balanced" */
export function traitLine(traits: DisplayTraits): string {
  return TRAIT_ORDER.map((key) => `${TRAIT_LABELS[key]}: ${traitValueLabel(key, traits)}`).join(' · ')
}
